import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { Add } from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import {
  DealQuestionnaireQuestionSettings,
  Responses
} from "@/pages/deals/types";

const TRANSLATION_PREFIX = "deals.dealView.pocSuccessCriteria";

interface Props {
  pocName?: string;
  hasNoSuccessCriteria: boolean;
  onAddSuccessCriteria: () => void;
  className?: string;
  pocQuestions?: DealQuestionnaireQuestionSettings[] | null;
  pocResponses?: Responses;
}

const POCSuccessCriteria: React.FC<Props> = ({
  pocName,
  hasNoSuccessCriteria,
  onAddSuccessCriteria,
  className,
  pocQuestions,
  pocResponses
}) => {
  const { t } = useTranslation();

  const successCriteriaQuestions = useMemo(() => {
    if (!pocQuestions) return [];
    return pocQuestions.filter((q) => {
      if (!q.isSuccessCriteria) return false;
      // Check if the checkbox is checked (selected)
      const response = pocResponses?.[q.id];
      return Boolean(response?.response?.value) || q.isMandatory;
    });
  }, [pocQuestions, pocResponses]);

  return (
    <div
      className={cn(
        "flex flex-col gap-4 p-4 mb-6 bg-surface-elevation2 shadow-popUp rounded-lg border border-border-border1",
        className
      )}
    >
      <div className="flex w-full items-center justify-between">
        <Text variant="label">
          {t(`${TRANSLATION_PREFIX}.title`, {
            pocName: pocName || ""
          })}
        </Text>
        {!hasNoSuccessCriteria && (
          <Button variant="tertiary" onClick={onAddSuccessCriteria}>
            {t(`${TRANSLATION_PREFIX}.updateCriteria`)}
          </Button>
        )}
      </div>
      {hasNoSuccessCriteria ? (
        <div className="flex flex-col justify-center items-center py-4 gap-2">
          <Text variant="bodySecondary">
            {t(`${TRANSLATION_PREFIX}.subtitle`)}
          </Text>
          <Button variant="primary" onClick={onAddSuccessCriteria}>
            <Add />
            {t(`${TRANSLATION_PREFIX}.button`)}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {successCriteriaQuestions.map((question) => {
            const response = pocResponses?.[question.id];
            const innerValue = (response?.response as any)?.innerValue;
            const unit = question.responseType?.unit;
            const hasInnerValue =
              innerValue !== undefined &&
              innerValue !== null &&
              innerValue !== "";

            return (
              <div key={question.id} className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-icon-positive mt-2 flex-shrink-0" />
                <Text variant="bodySecondary">
                  {question.questionText}
                  {hasInnerValue && (
                    <>
                      {t(` ${TRANSLATION_PREFIX}.above`)} {innerValue}
                      {unit || ""}
                    </>
                  )}
                </Text>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default POCSuccessCriteria;
